/* eslint-disable react/display-name */
import React, {
  memo,
  useMemo,
  useContext,
  useState,
  useEffect }                           from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import PropTypes                        from 'prop-types';
import clsx                             from 'clsx';
import { useTranslation }               from 'react-i18next';
import { withRouter }                   from 'react-router-dom';
import TablePagination                  from '@material-ui/core/TablePagination';
import Paper, { paperPropTypes }        from '~/components/Paper';
import Filter                           from '~/containers/Filter';
import Request                          from '~/providers/Request';
import Route                            from '~/providers/Route';
import toInt                            from '~/utils/Number/toInt';
import Column                           from '../../contexts/Column';
import makeDefaultActions               from './utils/makeDefaultActions';
import makeQueryRequest                 from './utils/makeQueryRequest';
import MTableAction                     from './components/MTableAction';
import useStyles                        from './styles';

const PER_PAGE_DEFAULT = 20;

function Table(props) {
  const {
    history,
    paperProps,
    actions,
    extraActions,
    path,
    recordMap,
    deleteAction,
    routeToNew,
    routeToEdit,
    routeToShow,
    routeToExams,
    cancelModalAction,
    listEl,
    context,
    fixedParams,
    queryToShow,
    pushModalAction,
    modalEditAction,
    modalShowAction,
    modalNewAction,
    modalMedicAction,
    fixedQuery,
    fetchFlag,
    onExport,
    ...ownProps
  } = props;
  const query = history.location.route.getQuery();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { data, addRecords } = useContext(context);
  const { t } = useTranslation();
  const classes = useStyles();
  const { columns } = useContext(Column);

  if(!query.per_page)
    query.per_page = PER_PAGE_DEFAULT;

  const changeQS = (qs, pageSize, filter) => setQuery({search: qs, pageSize}, false, filter);
  const eraseQS = (pageSize) =>setQuery({pageSize}, true);

  const defaultActions = useMemo(
    () => makeDefaultActions({
      t,
      actionClass: {
        edit: clsx(classes.action, classes.actionEdit),
        modalPush: clsx(classes.action, classes.actionPush),
        del: clsx(classes.action, classes.actionDel),
        show: clsx(classes.action, classes.actionShow),
        modalEdit: clsx(classes.action, classes.actionEdit),
        modalShow: clsx(classes.action, classes.actionShow),
        modalMedic: clsx(classes.action, classes.actionModalMedic),
        exams: clsx(classes.action, classes.actionExam),
      },
      listEl,
      routeToNew,
      routeToEdit,
      deleteAction,
      pushModalAction,
      cancelModalAction,
      routeToShow,
      routeToExams,
      queryToShow,
      modalEditAction,
      modalShowAction,
      modalNewAction,
      modalMedicAction,
      filterSize: query.size,
      setOpenFilter: setOpen,
      changeQS,
      eraseQS,
      history,
      queryString: query.qs,
      onExport,
    }),
    [
      t,
      routeToNew,
      routeToEdit,
      deleteAction,
      routeToShow,
      routeToExams,
      query.size,
    ]
  );
  const actionsActives = [
    ...actions.map(action => defaultActions[action]),
    ...extraActions,
  ];

  function setQuery(newQuery, eraseSearch=false, filter) {
    if(eraseSearch)
      delete query.qs;

    history.replace(
      history.location.route.setQuery({
        ...query,
        filters: filter,
        ...makeQueryRequest(newQuery),
      }, fixedParams)
    );
  }

  function fetchRecords() {
    setLoading(true);
    const defaultQuery = { page: 1, per_page: PER_PAGE_DEFAULT };

    Request.get(path, { ...defaultQuery, ...query, ...fixedQuery })
      .then(({ data, meta }) => ({
        data,
        meta,
        page: toInt(meta.page) - 1,
        totalCount: toInt(meta.total),
      }))
      .then(addRecords)
      .then(() => {
        setLoading(false);
      })
      .catch(() => ({
        data: [],
        page: 0,
        totalCount: 0,
      }));
  }

  useEffect(() =>{
    let isSubscribed = true;
    if(isSubscribed){
      fetchRecords();
    }
    return () => (isSubscribed = false);

  },
    [
      query.page,
      query.per_page,
      query.qs,
      query.filters,
      query.sorter,
      query.size,
      fetchFlag,
    ],
  );

  useEffect(() => {
    if (listEl)
      listEl.current.onQueryChange();
  }, [data]);

  return (
    <>
      <MaterialTable
        tableRef={listEl}
        localization={t('materialTable', { returnObjects: true })}
        actions={actionsActives} //check this issue https://github.com/mbrn/material-table/issues/657
        isLoading={loading}
        onChangeRowsPerPage={(pageSize) => setQuery({
          filters: query.filters,
          pageSize
        })}
        onChangePage={(page) => setQuery({
          filters: query.filters,
          page,
          pageSize: query.per_page
        })}
        onOrderChange={(orderBy, orderDirection) =>
          setQuery({
            filters: query.filters,
            pageSize: query.per_page,
            orderBy: columns[orderBy],
            orderDirection
          })
        }
        {...ownProps}
        columns={columns.map( e => ({
          ...e,
          type: e.type === 'async' ? 'numeric' : e.type,
        }) )}
        data={() => new Promise(resolve =>{
          resolve({
            ...data,
            data: [...data.data].map(recordMap)
          });
        }
        )}
        options={{
          search:false,
          loadingType: 'linear',
          showTitle: false,
          exportButton: false,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [5, 10, 20, 50, 100, 200],
          initialPage: 0,
          pageSize: parseInt(query.per_page || PER_PAGE_DEFAULT),
          ...ownProps.options,
        }}
        components={{
          Action: props => <MTableAction {...props} />,
          Container: props => <Paper {...paperProps} {...props} />,
          Toolbar: props => <MTableToolbar {...props} classes={{ root: clsx('no-printable', classes.toolbar) }} />,
          Pagination: props => <TablePagination {...props} count={props.count || 0} classes={{ root: 'no-printable' }} />,
          ...ownProps.components,
        }}
      />

      <Filter
        filter={query.filters}
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        onFilter={(compress, json, size) => {
          setQuery({ filters: compress, size, pageSize: query.per_page, });

          setOpen(false);
        }}

        columns={columns.map(col => ({
          ...col,
          type: col.lookup ? 'options' : col.type,
        }))}
      />
    </>
  );
}

Table.defaultProps = {
  ...MaterialTable.defaultProps,
  actions: [
    'print',
    'showHide',
  ],
  extraActions: [],
  fixedQuery: {},
  a: false
};

const paperProps = {...paperPropTypes};
delete paperProps.children;

Table.propTypes = {
  ...MaterialTable.propTypes,
  paperProps: PropTypes.shape(paperProps).isRequired,
  path: PropTypes.string.isRequired,
  routeToNew: PropTypes.instanceOf(Route),
  routeToEdit: PropTypes.instanceOf(Route),
  routeToShow: PropTypes.instanceOf(Route),
  routeToExams: PropTypes.instanceOf(Route),
  deleteAction: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.oneOf([
      'export',
      'print',
      'showHide',
      'filter',
      'new',
      'edit',
      'del',
      'show',
      'modalPush',
      'modalCancel',
      'modalMedic',
      'editPush',
      'modalEdit',
      'modalShow',
      'modalNew',
      'search',
      'exams'
    ])
  ),
  pushModalAction: PropTypes.func,
  modalEditAction: PropTypes.func,
  modalShowAction: PropTypes.func,
  modalMedicAction: PropTypes.func,
  extraActions: PropTypes.arrayOf(PropTypes.object),
  a: PropTypes.bool,
};

export default memo(withRouter(Table));
